/**
 * API Client - HTTP client for the Love Map backend REST API
 *
 * Provides the same data interface as placesService but fetches from the
 * backend API instead of static JSON. Used when app.config.data.fetchMode
 * is set to 'api'.
 */

import type { Place } from '../types/Place';
import type { MapSettings, MapCollaborator } from '@love-map/shared';

/**
 * API configuration
 */
const getBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
};

/**
 * Token management
 */
const TOKEN_STORAGE_KEY = 'love-map-api-token';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage not available (e.g., SSR)
  }
}

export function clearStoredToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // localStorage not available
  }
}

/**
 * Build request headers, optionally including auth token
 */
function buildHeaders(includeAuth = false): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getStoredToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${getBaseUrl()}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...buildHeaders(options.method !== 'GET'),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message = (body as Record<string, unknown>).error || response.statusText;
    throw new Error(`API Error ${response.status}: ${message}`);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * API response types (matching backend PlaceResponse / PlacesListResponse)
 */
interface PlacesListApiResponse {
  success: boolean;
  data: Place[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

interface PlaceApiResponse {
  success: boolean;
  data: Place;
}

interface AuthLoginResponse {
  success: boolean;
  token: string;
  expiresIn: string;
}

interface AuthLoginWithEmailResponse {
  success: boolean;
  token: string;
  expiresIn: string;
  user: {
    id: string;
    email: string;
    emailVerified: boolean;
    subscriptionStatus: string;
    onboardingCompleted: boolean;
    createdAt: string;
  };
}

interface AuthVerifyResponse {
  success: boolean;
  valid: boolean;
  user?: {
    sub: string;
    email: string;
    role: string;
  };
}

/**
 * Map entity returned from the API
 */
export interface Map {
  id: string;
  userId: string;
  title: string;
  description: string | null;
  settings: MapSettings;
  isPublic: boolean;
  publicSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MapsListApiResponse {
  success: boolean;
  data: Map[];
}

// ──────────────── Places API ────────────────

/**
 * Fetch all places from the API.
 * Uses a large pageSize to fetch all at once (matching static behavior).
 */
export async function fetchPlacesFromApi(): Promise<Place[]> {
  const response = await apiFetch<PlacesListApiResponse>(
    '/places?pageSize=1000&sortBy=order&sortOrder=asc',
  );
  return response.data;
}

/**
 * Fetch a single place by ID
 */
export async function fetchPlaceById(id: string): Promise<Place> {
  const response = await apiFetch<PlaceApiResponse>(`/places/${id}`);
  return response.data;
}

/**
 * Create a new place (requires auth)
 */
export async function createPlace(
  place: Omit<Place, 'id' | 'order'>,
  mapId?: string,
): Promise<Place> {
  const response = await apiFetch<PlaceApiResponse>('/places', {
    method: 'POST',
    body: JSON.stringify({ ...place, mapId }),
  });
  return response.data;
}

/**
 * Update an existing place (requires auth)
 */
export async function updatePlace(
  id: string,
  updates: Partial<Omit<Place, 'id' | 'order'>>,
): Promise<Place> {
  const response = await apiFetch<PlaceApiResponse>(`/places/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.data;
}

/**
 * Delete a place (requires auth)
 */
export async function deletePlace(id: string): Promise<void> {
  await apiFetch<void>(`/places/${id}`, {
    method: 'DELETE',
  });
}

// ──────────────── Auth API ────────────────

/**
 * Login with the secret date and store the token
 */
export async function login(secretDate: string): Promise<AuthLoginResponse> {
  const response = await apiFetch<AuthLoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ secretDate }),
    headers: { 'Content-Type': 'application/json' },
  });

  if (response.token) {
    setStoredToken(response.token);
  }

  return response;
}

/**
 * Login with email and password and store the token
 */
export async function loginWithEmail(
  email: string,
  password: string,
): Promise<AuthLoginWithEmailResponse> {
  const response = await apiFetch<AuthLoginWithEmailResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    headers: { 'Content-Type': 'application/json' },
  });

  if (response.token) {
    setStoredToken(response.token);
  }

  return response;
}

/**
 * Register a new user with email and password and store the token
 */
export async function registerUser(
  email: string,
  password: string,
): Promise<AuthLoginWithEmailResponse> {
  const response = await apiFetch<AuthLoginWithEmailResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    headers: { 'Content-Type': 'application/json' },
  });

  if (response.token) {
    setStoredToken(response.token);
  }

  return response;
}

/**
 * Verify the currently stored token
 */
export async function verifyToken(): Promise<AuthVerifyResponse> {
  const token = getStoredToken();
  if (!token) {
    return { success: true, valid: false };
  }

  const response = await apiFetch<AuthVerifyResponse>('/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.valid) {
    clearStoredToken();
  }

  return response;
}

/**
 * Logout by clearing the stored token
 */
export function logout(): void {
  clearStoredToken();
}

/**
 * Request a password reset email
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiFetch<{ success: boolean }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Reset password using a token from the reset email
 */
export async function resetPassword(token: string, password: string): Promise<void> {
  await apiFetch<{ success: boolean }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
    headers: { 'Content-Type': 'application/json' },
  });
}

// ──────────────── Users API ────────────────

/**
 * User profile response
 */
export interface UserProfile {
  id: string;
  email: string;
  emailVerified: boolean;
  subscriptionStatus: string;
  onboardingCompleted: boolean;
  createdAt: string;
}

interface UserProfileResponse {
  success: boolean;
  user: UserProfile;
}

/**
 * Get the current user's profile
 */
export async function getCurrentUser(): Promise<UserProfile> {
  const response = await apiFetch<UserProfileResponse>('/users/me', {
    headers: buildHeaders(true),
  });
  return response.user;
}

/**
 * Mark the current user's onboarding as complete
 */
export async function completeOnboarding(): Promise<void> {
  await apiFetch<{ success: boolean }>('/users/me/onboarding-complete', {
    method: 'POST',
    headers: buildHeaders(true),
  });
}

// ──────────────── Photos API ────────────────

/**
 * Photo upload response from the API
 */
export interface PhotoUploadResponse {
  url: string;
  key: string;
}

/**
 * Upload a photo to R2 storage
 * Uses FormData for multipart upload
 */
export async function uploadPhoto(file: File): Promise<PhotoUploadResponse> {
  const url = `${getBaseUrl()}/photos`;
  const formData = new FormData();
  formData.append('photo', file);

  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message = (body as Record<string, unknown>).error || response.statusText;
    throw new Error(`API Error ${response.status}: ${message}`);
  }

  const data = await response.json();
  return { url: data.url, key: data.key };
}

/**
 * Delete a photo from R2 storage
 */
export async function deletePhoto(key: string): Promise<void> {
  await apiFetch<void>(`/photos/${key}`, {
    method: 'DELETE',
    headers: buildHeaders(true),
  });
}

// ──────────────── Maps API ────────────────

/**
 * Public map response type
 */
interface PublicMapApiResponse {
  success: boolean;
  data: {
    map: Map;
    places: Place[];
  };
}

/**
 * Fetch a public map by its public slug (no auth required)
 */
export async function fetchPublicMap(
  slug: string,
): Promise<{ map: Map; places: Place[] }> {
  const response = await apiFetch<PublicMapApiResponse>(`/maps/public/${slug}`);
  return response.data;
}

/**
 * Fetch all maps for the authenticated user
 */
export async function fetchMaps(): Promise<Map[]> {
  const response = await apiFetch<MapsListApiResponse>('/maps', {
    headers: buildHeaders(true),
  });
  return response.data;
}

interface MapApiResponse {
  success: boolean;
  data: Map;
}

/**
 * Toggle a map's public visibility
 */
export async function toggleMapPublic(
  mapId: string,
  isPublic: boolean,
): Promise<Map> {
  const response = await apiFetch<MapApiResponse>(`/maps/${mapId}/public`, {
    method: 'POST',
    body: JSON.stringify({ isPublic }),
    headers: buildHeaders(true),
  });
  return response.data;
}

/**
 * Update map settings (including theme)
 * SPRINT-5.5: Used for saving theme customization
 */
export async function updateMapSettings(
  mapId: string,
  settings: Partial<MapSettings>,
): Promise<Map> {
  const response = await apiFetch<MapApiResponse>(`/maps/${mapId}`, {
    method: 'PUT',
    body: JSON.stringify({ settings }),
    headers: buildHeaders(true),
  });
  return response.data;
}

/**
 * Create a new map
 * SPRINT-5.8: Create new map from dashboard
 */
export async function createMap(
  title: string,
  description?: string,
): Promise<Map> {
  const response = await apiFetch<MapApiResponse>('/maps', {
    method: 'POST',
    body: JSON.stringify({ title, description }),
    headers: buildHeaders(true),
  });
  return response.data;
}

/**
 * Export format type
 * SPRINT-8.6: Export map data
 */
export type ExportFormat = 'json' | 'geojson';

/**
 * Export a map and all its places
 * SPRINT-8.6: Export map data
 */
export async function exportMap(mapId: string, format: ExportFormat = 'json'): Promise<Blob> {
  const url = `${getBaseUrl()}/maps/${mapId}/export?format=${format}`;

  const response = await fetch(url, {
    headers: buildHeaders(true),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message = (body as Record<string, unknown>).error || response.statusText;
    throw new Error(`API Error ${response.status}: ${message}`);
  }

  return response.blob();
}

/**
 * Download a blob as a file
 * Helper function for export functionality
 * SPRINT-8.6: Export map data
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ──────────────── Analytics API ────────────────

/**
 * Map analytics data
 * SPRINT-8.9: Map view analytics
 */
export interface MapAnalytics {
  totalViews: number;
  viewsLast7Days: number;
  viewsLast30Days: number;
  dailyViews: { date: string; count: number }[];
}

interface AnalyticsApiResponse {
  success: boolean;
  data: MapAnalytics;
}

/**
 * Fetch analytics data for a map
 * SPRINT-8.9: Map view analytics
 */
export async function fetchMapAnalytics(mapId: string): Promise<MapAnalytics> {
  const response = await apiFetch<AnalyticsApiResponse>(`/maps/${mapId}/analytics`, {
    headers: buildHeaders(true),
  });
  return response.data;
}

// ──────────────── Billing API ────────────────

/**
 * Checkout session response type
 */
interface CheckoutSessionResponse {
  success: boolean;
  url: string;
}

/**
 * Create a Stripe checkout session for Pro subscription
 * SPRINT-6.6: Used for upgrade prompts
 */
export async function createCheckoutSession(): Promise<{ url: string }> {
  const response = await apiFetch<CheckoutSessionResponse>('/billing/checkout', {
    method: 'POST',
    headers: buildHeaders(true),
  });
  return { url: response.url };
}

/**
 * Billing portal session response type
 */
interface BillingPortalResponse {
  success: boolean;
  url: string;
}

/**
 * Create a Stripe Billing Portal session for managing subscription
 * SPRINT-6.7: Used for settings page billing management
 */
export async function createBillingPortalSession(): Promise<{ url: string }> {
  const response = await apiFetch<BillingPortalResponse>('/billing/portal', {
    method: 'POST',
    headers: buildHeaders(true),
  });
  return { url: response.url };
}

/**
 * Check if an error indicates an upgrade is required
 * Returns the limit type if upgrade is needed, null otherwise
 */
export function isUpgradeRequiredError(error: unknown): 'maps' | 'places' | 'storage' | null {
  if (!(error instanceof Error)) return null;

  const message = error.message.toLowerCase();

  // Check for 403 status with upgrade-related messages
  if (!message.includes('403')) return null;

  if (message.includes('map') && (message.includes('limit') || message.includes('upgrade'))) {
    return 'maps';
  }
  if (message.includes('place') && (message.includes('limit') || message.includes('upgrade'))) {
    return 'places';
  }
  if (message.includes('storage') && (message.includes('limit') || message.includes('upgrade'))) {
    return 'storage';
  }

  // Generic upgrade required
  if (message.includes('upgrade')) {
    return 'maps';
  }

  return null;
}

// ──────────────── Admin API ────────────────

/**
 * Admin user data returned from the API
 */
export interface AdminUser {
  id: string;
  email: string;
  createdAt: string;
  subscriptionStatus: string;
  emailVerified: boolean;
  mapCount: number;
}

/**
 * Admin metrics data returned from the API
 * SPRINT-7.4: Admin dashboard metrics
 */
export interface AdminMetrics {
  totalUsers: number;
  proUsers: number;
  totalMaps: number;
  totalPlaces: number;
  totalStorageBytes: number;
}

interface AdminUsersResponse {
  success: boolean;
  users: AdminUser[];
}

interface AdminMetricsResponse {
  success: boolean;
  metrics: AdminMetrics;
}

/**
 * Fetch all users (admin only)
 * SPRINT-7.3: Admin dashboard user list
 */
export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const response = await apiFetch<AdminUsersResponse>('/admin/users', {
    headers: buildHeaders(true),
  });
  return response.users;
}

/**
 * Fetch platform metrics (admin only)
 * SPRINT-7.4: Admin dashboard metrics
 */
export async function fetchAdminMetrics(): Promise<AdminMetrics> {
  const response = await apiFetch<AdminMetricsResponse>('/admin/metrics', {
    headers: buildHeaders(true),
  });
  return response.metrics;
}

/**
 * Check if the current user has admin role
 * Returns true if the user's JWT contains role='admin'
 */
export function isCurrentUserAdmin(): boolean {
  const token = getStoredToken();
  if (!token) return false;

  try {
    // Decode JWT payload (base64url)
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.role === 'admin';
  } catch {
    return false;
  }
}

// ──────────────── Import API ────────────────

/**
 * Import response from the API
 * SPRINT-8.7: Import places functionality
 */
export interface ImportPlacesResponse {
  success: boolean;
  imported: number;
  errors: string[];
}

/**
 * Import places data - can be places array or GeoJSON
 */
export type ImportPlacesData =
  | { places: Record<string, unknown>[] }
  | { type: 'FeatureCollection'; features: Record<string, unknown>[] }
  | Record<string, unknown>[];

/**
 * Import places from JSON or GeoJSON data
 * SPRINT-8.7: Import places functionality
 */
export async function importPlaces(
  mapId: string,
  data: ImportPlacesData,
): Promise<ImportPlacesResponse> {
  const response = await apiFetch<ImportPlacesResponse>(`/maps/${mapId}/import`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: buildHeaders(true),
  });
  return response;
}

// ──────────────── Collaborators API ────────────────

/**
 * Collaborator type representing a user's access to a shared map
 * Uses MapCollaborator from shared package for consistency
 * SPRINT-8.4: Collaborator management UI
 */
export type Collaborator = MapCollaborator;

interface CollaboratorsListResponse {
  success: boolean;
  data: Collaborator[];
}

interface CollaboratorResponse {
  success: boolean;
  data: Collaborator;
}

interface AcceptInviteResponse {
  success: boolean;
  message: string;
  redirectUrl?: string;
}

/**
 * Fetch all collaborators for a map
 * SPRINT-8.4: Collaborator management UI
 */
export async function fetchCollaborators(mapId: string): Promise<Collaborator[]> {
  const response = await apiFetch<CollaboratorsListResponse>(
    `/maps/${mapId}/collaborators`,
    {
      headers: buildHeaders(true),
    }
  );
  return response.data;
}

/**
 * Invite a collaborator to a map by email
 * SPRINT-8.4: Collaborator management UI
 */
export async function inviteCollaborator(
  mapId: string,
  email: string,
  role: 'editor' | 'viewer'
): Promise<Collaborator> {
  const response = await apiFetch<CollaboratorResponse>(
    `/maps/${mapId}/collaborators`,
    {
      method: 'POST',
      body: JSON.stringify({ email, role }),
      headers: buildHeaders(true),
    }
  );
  return response.data;
}

/**
 * Remove a collaborator from a map
 * SPRINT-8.4: Collaborator management UI
 */
export async function removeCollaborator(
  mapId: string,
  collaboratorId: string
): Promise<void> {
  await apiFetch<void>(`/maps/${mapId}/collaborators/${collaboratorId}`, {
    method: 'DELETE',
    headers: buildHeaders(true),
  });
}

/**
 * Accept a collaborator invitation
 * SPRINT-8.4: Collaborator management UI
 */
export async function acceptInvite(token: string): Promise<{ redirectUrl: string }> {
  const response = await apiFetch<AcceptInviteResponse>(`/invites/${token}/accept`, {
    method: 'POST',
    headers: buildHeaders(true),
  });
  return { redirectUrl: response.redirectUrl || '/dashboard' };
}
